import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import useFoundingSpots from '../../hooks/useFoundingSpots';
import WatchOnRoku from '../WatchOnRoku';
import TrialButton from '../TrialSignup/TrialButton';
import './PremiumHero.css';
import { STARTING_PRICE, TRIAL_DAYS, TRIAL_TRUST_ITEMS } from '../../utils/offerTerms';

// The hero video autoplays only where it's cheap and welcome: not on phones,
// data-saver connections, or for people who prefer reduced motion. There it
// shows the poster with a play button instead of pulling the video on load.
function shouldAutoplayHeroVideo() {
  if (typeof window === 'undefined') return false;
  try {
    if (navigator.connection && navigator.connection.saveData) return false;
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (window.matchMedia('(max-width: 767px)').matches) return false;
    }
  } catch (_) {
    return false;
  }
  return true;
}

const PremiumHero = () => {
  const { currentUser } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showDemoModal, setShowDemoModal] = useState(false);
  const demoVideoRef = useRef(null);
  const heroVideoRef = useRef(null);
  const [autoplayHero] = useState(shouldAutoplayHeroVideo);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const spots = useFoundingSpots();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowDemoModal(false);
    };
    if (showDemoModal) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      if (demoVideoRef.current) {
        demoVideoRef.current.pause();
      }
    };
  }, [showDemoModal]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  return (
    <section className="premium-hero" onMouseMove={handleMouseMove}>
      {/* Animated mesh background */}
      <div className="hero-mesh-bg">
        <div
          className="mesh-gradient mesh-1"
          style={{
            transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`
          }}
        />
        <div
          className="mesh-gradient mesh-2"
          style={{
            transform: `translate(${-mousePosition.x * 15}px, ${-mousePosition.y * 15}px)`
          }}
        />
        <div
          className="mesh-gradient mesh-3"
          style={{
            transform: `translate(${mousePosition.x * 10}px, ${-mousePosition.y * 10}px)`
          }}
        />
      </div>

      {/* Grid pattern overlay */}
      <div className="hero-grid-pattern" />

      {/* Floating particles */}
      <div className="floating-particles">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              '--delay': `${Math.random() * 5}s`,
              '--duration': `${15 + Math.random() * 10}s`,
              '--x-start': `${Math.random() * 100}%`,
              '--y-start': `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div className="hp-hero-container">
        <div className={`hp-hero-content ${isLoaded ? 'loaded' : ''}`}>
          {/* Badge — the offer, said first: the trial is the way in. The
              Founding Member rate is a small line under the buttons. */}
          <div className="hero-badge-premium">
            <span>For coaches and consultants who teach to sell</span>
            <span className="badge-live-dot" />
          </div>

          {/* "Now on Roku" — renders only when the channel is live and
              REACT_APP_ROKU_CHANNEL_URL is set, so it stays hidden until
              Roku certification approves CANIRA TV. */}
          <WatchOnRoku variant="badge" label="Now on Roku" className="hero-roku-badge" />

          {/* Main heading — states outright what Canira IS, so the brand
              isn't pinned to "webinar". */}
          <h1 className="hp-hero-title hero-title-umbrella">
            Turn one workshop into <span className="gradient-text">a week of marketing.</span>
          </h1>

          {/* Subheading — names the actual integrated workflow.
              Record once → go live, run evergreen, clip everything,
              sell directly. This is Canira's real wedge and what
              shipped features genuinely deliver. Avoids the email-
              sequence claim (no editor exists) and the per-webinar
              paid-toggle claim (column dead-wired). Founder-voice
              closer names the pain ("stitching tools together")
              the ICP feels. "Webinar and podcast platform" is the
              primary SEO keyword — it must appear in visible
              above-the-fold copy, not just meta tags. */}
          <p className="hp-hero-subtitle">
            Run it live or record it once. Canira turns it into an automated workshop that keeps
            taking sign-ups, plus clips, posts and follow-up emails, so more people see your best
            teaching and take the next step with you.
          </p>

          {/* Stats — beta-stage scarcity signals, not feature dumps.
              These are the numbers that actually mean something to a
              prospect: how many spots, what's the discount, what does
              the consolidation look like. */}
          <div className="hero-stats-grid">
            <div className="hero-stat">
              <span className="hp-stat-value">
                {TRIAL_DAYS}
              </span>
              <span className="hp-stat-label">Days free</span>
            </div>
            <div className="hero-stat">
              <span className="hp-stat-value">
                ${STARTING_PRICE}
              </span>
              <span className="hp-stat-label">Founding rate</span>
            </div>
            <div className="hero-stat">
              <span className="hp-stat-value">
                16
              </span>
              <span className="hp-stat-label">Posts from one workshop</span>
              <Link to="/features/repurposing" className="stat-detail">
                See what our Oct 20 workshop made
              </Link>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="hero-cta-group">
            {currentUser ? (
              <>
                <Link to="/dashboard" className="premium-cta-btn primary">
                  <span>Go to Dashboard</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </Link>
                <button onClick={() => setShowDemoModal(true)} className="premium-cta-btn secondary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
                  </svg>
                  <span>Watch Demo</span>
                </button>
              </>
            ) : (
              <>
                <TrialButton className="premium-cta-btn primary">
                  <span>Start {TRIAL_DAYS}-day trial</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </TrialButton>
                <button onClick={() => setShowDemoModal(true)} className="premium-cta-btn secondary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
                  </svg>
                  <span>Watch Demo</span>
                </button>
              </>
            )}
          </div>

          <p className="hero-recording-line" style={{ margin: '14px 0 0', fontWeight: 600 }}>
            Already have a webinar, workshop or Zoom recording? Start with that.
          </p>
          {/* Founding Member — a small line while spots remain, with the real
              count (GET /api/founding/status). */}
          {!spots.isFull && (
          <div className="hero-cta-tertiary">
            <Link to="/pricing" className="hero-cta-tertiary-link">
              Founding Member rate, locked for life
              {spots.remaining != null ? ` · ${spots.remaining} of ${spots.cap} spots left` : ''}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
          )}

          {/* Trust badges */}
          <div className="hero-trust-badges">
            {TRIAL_TRUST_ITEMS.map((item) => (
              <div className="hp-trust-item" key={item}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hero Visual.
            Floating cards (testimonial, live, stats) were removed
            because they covered ~70% of the demo video underneath
            and duplicated the hero copy on the left. The video is
            the proof point — the eye should land on it, not on
            decorative chrome. If a small "live now" indicator
            becomes useful later, add it inside the visual-main
            container so it sits ON the video frame, not over it. */}
        <div className={`hero-visual ${isLoaded ? 'loaded' : ''}`}>
          <div className="visual-container">
            <div className="visual-main">
              <div className="visual-glow" />
              {/* The hero loop (16s, ~1.15 MB, silent): go live -> AI clips ->
                  replay filling seats -> follow-up -> podcast + course -> new
                  enrollments, ending on the tagline. Rendered from
                  demo-video/src/HomeHero.tsx (see the header there). It shows NO
                  prices on purpose: the old loop's "$49.00 · your price" course
                  card sat beside the starting price above and read as what
                  Canira costs. The full walkthrough stays behind "Watch demo"
                  (the modal below). No gradient over it: the captions are at the
                  bottom of the frame. */}
              <video
                ref={heroVideoRef}
                src="/canira-homepage-hero-v2.mp4"
                poster="/canira-homepage-hero-v2-poster.jpg"
                autoPlay={autoplayHero}
                muted
                loop
                playsInline
                preload={autoplayHero ? 'metadata' : 'none'}
                className="visual-image"
                aria-label="Canira demo: go live once, then AI clips, a replay that keeps filling seats, follow-up emails, a podcast episode and a course"
                onPlay={() => setHeroPlaying(true)}
              />
              {!autoplayHero && !heroPlaying && (
                <button
                  type="button"
                  className="hero-video-play"
                  aria-label="Play the demo"
                  onClick={() => heroVideoRef.current?.play()?.catch?.(() => {})}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <polygon points="8 5 19 12 8 19 8 5" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="scroll-indicator">
        <div className="scroll-mouse">
          <div className="scroll-wheel" />
        </div>
        <span>Scroll to explore</span>
      </div>

      {/* Demo Video Modal */}
      {showDemoModal && (
        <div className="demo-modal-overlay" onClick={() => setShowDemoModal(false)}>
          <div className="demo-modal" onClick={(e) => e.stopPropagation()}>
            <button className="demo-modal-close" onClick={() => setShowDemoModal(false)} aria-label="Close demo video">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="demo-modal-header">
              <h3>See Canira in Action</h3>
              <p>See how people produce, distribute, and monetize their content from one platform — captions on, sound off by design.</p>
            </div>
            <div className="demo-video-container">
              <video
                ref={demoVideoRef}
                src="/canira-homepage-demo.mp4"
                poster="/canira-homepage-poster.jpg"
                controls
                autoPlay
                muted
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%', borderRadius: '12px' }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PremiumHero;
